import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, Menu, ChevronDown, Phone, ShieldCheck, Wrench, Tag } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CATEGORY_GROUPS, ACTIVE_BRANDS } from "@/data/catalog";
import { SITE } from "@/data/site";
import { useCart } from "@/context/CartContext";

function SearchForm({ onSubmit, testId }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
    if (onSubmit) onSubmit();
  };
  return (
    <form onSubmit={submit} className="relative w-full" role="search">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search model #, brand, equipment…" aria-label="Search equipment" className="rounded-sm border-slate-300 pl-9" data-testid={testId} />
    </form>
  );
}

const navLink = "text-sm font-semibold text-slate-700 transition-colors duration-200 hover:text-sky-700";

export default function Header() {
  const { count } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-slate-950 text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 text-xs sm:px-6 lg:px-8">
          <a href={SITE.phoneHref} className="inline-flex items-center gap-1.5 font-medium transition-colors duration-200 hover:text-sky-300" data-testid="topbar-phone">
            <Phone className="h-3.5 w-3.5" />{SITE.phone}
          </a>
          <span className="hidden items-center gap-4 text-slate-300 sm:flex">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-sky-400" />Manufacturer warranty available</span>
            <span className="inline-flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5 text-orange-400" />Equipment purchasing assistance</span>
          </span>
        </div>
      </div>

      <div className="border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Logo />

          <nav className="ml-6 hidden items-center gap-5 lg:flex" aria-label="Primary">
            <Link to="/shop" className={navLink} data-testid="nav-shop">Shop</Link>

            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700 outline-none transition-colors duration-200 hover:text-sky-700" data-testid="nav-categories">
                Categories <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[720px] rounded-lg p-5">
                <div className="grid grid-cols-4 gap-5">
                  {CATEGORY_GROUPS.map((g) => (
                    <div key={g.label}>
                      <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{g.label}</div>
                      <div className="flex flex-col">
                        {g.categories.filter((c) => !c.hidden).map((c) => (
                          <DropdownMenuItem key={c.slug} asChild className="rounded-sm">
                            <Link to={`/category/${c.slug}`} data-testid={`nav-category-${c.slug}`} className="text-sm text-slate-700">{c.name}</Link>
                          </DropdownMenuItem>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700 outline-none transition-colors duration-200 hover:text-sky-700" data-testid="nav-brands">
                Brands <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48 rounded-sm">
                {ACTIVE_BRANDS.map((b) => (
                  <DropdownMenuItem key={b.slug} asChild>
                    <Link to={`/brand/${b.slug}`} data-testid={`nav-brand-${b.slug}`}>{b.name}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
<Link to="/about" className={navLink} data-testid="nav-about">About</Link>
            <Link to="/support" className={navLink} data-testid="nav-support">Support</Link>
            <Link to="/contact" className={navLink} data-testid="nav-contact">Contact</Link>
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <div className="hidden w-60 xl:block"><SearchForm testId="header-search-desktop" /></div>

            <Button asChild variant="ghost" size="icon" className="relative rounded-sm" data-testid="cart-button">
              <Link to="/cart" aria-label={`Cart, ${count} items`}>
                <ShoppingCart className="h-5 w-5" />
                {count > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 text-[11px] font-bold text-white" data-testid="cart-count">{count}</span>}
              </Link>
            </Button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-sm lg:hidden" aria-label="Open menu" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto p-0">
                <SheetHeader className="border-b border-slate-200 p-5 text-left"><SheetTitle><Logo compact /></SheetTitle></SheetHeader>
                <div className="p-5">
                  <SearchForm onSubmit={() => setMobileOpen(false)} testId="header-search-mobile" />
                  <nav className="mt-6 flex flex-col" aria-label="Mobile">
                    <SheetClose asChild><Link to="/shop" className="border-b border-slate-100 py-3 font-semibold text-slate-800" data-testid="mobile-nav-shop">Shop All Equipment</Link></SheetClose>
                    <Accordion type="multiple" className="w-full">
                      <AccordionItem value="categories" className="border-slate-100">
                        <AccordionTrigger className="py-3 font-semibold text-slate-800 hover:no-underline">Categories</AccordionTrigger>
                        <AccordionContent>
                          {CATEGORY_GROUPS.map((g) => (
                            <div key={g.label} className="mb-2">
                              <div className="py-1 text-[11px] font-bold uppercase tracking-widest text-slate-400">{g.label}</div>
                              {g.categories.filter((c) => !c.hidden).map((c) => (
                                <SheetClose asChild key={c.slug}><Link to={`/category/${c.slug}`} className="block py-2 text-sm text-slate-600" data-testid={`mobile-category-${c.slug}`}>{c.name}</Link></SheetClose>
                              ))}
                            </div>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value="brands" className="border-slate-100">
                        <AccordionTrigger className="py-3 font-semibold text-slate-800 hover:no-underline">Brands</AccordionTrigger>
                        <AccordionContent>
                          {ACTIVE_BRANDS.map((b) => (
                            <SheetClose asChild key={b.slug}><Link to={`/brand/${b.slug}`} className="block py-2 text-sm text-slate-600" data-testid={`mobile-brand-${b.slug}`}>{b.name}</Link></SheetClose>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    {[{ to: "/support", label: "Support / FAQ" }, { to: "/about", label: "About" }, { to: "/contact", label: "Contact" }].map((l) => (
                      <SheetClose asChild key={l.to}><Link to={l.to} className="border-b border-slate-100 py-3 font-semibold text-slate-800">{l.label}</Link></SheetClose>
                    ))}
                  </nav>
                  <a href={SITE.phoneHref} className="mt-6 inline-flex items-center gap-2 font-semibold text-sky-700"><Phone className="h-4 w-4" />{SITE.phone}</a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
